import type { BlobQueueStore } from '@jbroll/rowboat-client';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { onSynced } from '@/lib/syncSignal';
import { ANON_IDENTITY, MissingRowError, usePort, useRowboat } from '@/rowboat';
import { createPhotoQueue, ownedQueueStore, photoQueueStore } from '@/services/photoQueue';
import {
  canRetry,
  committed,
  drained,
  dropped,
  enqueued,
  failedCount,
  listed,
  pendingCount,
  type QueueState,
  retryDelayMs,
  started,
  type UploadStatus,
} from '@/services/uploadStatus';
import { Button, Drawer, Muted } from './ui';

interface PhotoUploads {
  enqueue: (photoId: string, bytes: Blob) => Promise<void>;
  retry: () => void;
  /** This device's queue state for a photo; undefined when nothing is queued here. */
  stateOf: (photoId: string) => QueueState | undefined;
  /** Bytes still queued on this device, for previews after a reload. */
  localBytes: (photoId: string) => Promise<Blob | undefined>;
  pending: number;
  failed: number;
  /** Queued here, but this identity's store has no row for them, so they can never upload. */
  orphans: readonly QueuedPhoto[];
  discard: (photoId: string) => Promise<void>;
  canUpload: boolean;
}

interface QueuedPhoto {
  photoId: string;
  bytes: Blob;
  queuedAt: number | undefined;
}

const offline = () => navigator.onLine === false;

// Two open tabs share the IndexedDB outbox; the lock keeps them from uploading every entry twice.
const DRAIN_LOCK = 'rosa-photo-upload-drain';

async function withDrainLock(fn: () => Promise<void>): Promise<void> {
  if (!navigator.locks) {
    throw new Error('navigator.locks (the Web Locks API) is required to upload photos');
  }
  await navigator.locks.request(DRAIN_LOCK, fn);
}

const Context = createContext<PhotoUploads | null>(null);

// One queue for the whole app, so the header counter, the inspector and the camera share
// per-photo state. Anonymous stores have no upload token, so their bytes wait for sign-up.
export function PhotoUploadsProvider({
  children,
  store: given,
}: {
  children: ReactNode;
  store?: BlobQueueStore;
}) {
  const graph = useRowboat();
  const { author, storedGroup } = usePort();
  const canUpload = author !== null;
  const owner = author ?? ANON_IDENTITY;
  const store = useMemo(
    () =>
      ownedQueueStore(given ?? photoQueueStore(), owner, (photoId) => !!graph.item_photo(photoId)),
    [given, owner, graph],
  );
  const [status, setStatus] = useState<UploadStatus>({});
  const [listedOnce, setListedOnce] = useState(false);
  const [orphans, setOrphans] = useState<readonly QueuedPhoto[]>([]);
  // Read by the start report and the key list, so a known orphan never shows as uploading.
  const orphanKeys = useRef<ReadonlySet<string>>(new Set());
  const queue = useMemo(
    () =>
      createPhotoQueue(graph, {
        ownerGroupId: (photoId) => storedGroup('item_photo', photoId),
        store,
        onEntry: (photoId, event) => {
          if (event === 'start' && orphanKeys.current.has(photoId)) return;
          setStatus((s) => (event === 'start' ? started(s, photoId) : committed(s, photoId)));
        },
      }),
    [graph, store, storedGroup],
  );

  const retryAttempt = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(retryTimer.current);
    };
  }, []);

  // The connection can drop during a drain; the online listener drains then.
  const scheduleRetry = useRef(() => {});
  scheduleRetry.current = () => {
    // A pass that scheduled a retry can still throw and schedule another; keep one handle.
    clearTimeout(retryTimer.current);
    if (!mounted.current || offline()) return;
    retryTimer.current = setTimeout(() => void drain(), retryDelayMs(retryAttempt.current++));
  };

  const pass = useRef(async () => {});
  pass.current = async () => {
    clearTimeout(retryTimer.current);
    const keys = await store.list();
    setStatus((s) =>
      listed(
        s,
        keys.filter((key) => !orphanKeys.current.has(key)),
      ),
    );
    setOrphans((o) => o.filter((entry) => keys.includes(entry.photoId)));
    setListedOnce(true);
    // Offline, a drain would only mark every entry failed; the online listener drains instead.
    if (!canUpload || offline()) return;
    const { uploaded, failed } = await queue.drain();
    for (const { key, error } of failed) console.warn('photo upload failed', key, error);
    const orphaned = failed.filter((f) => f.error instanceof MissingRowError).map((f) => f.key);
    const failedKeys = failed.map((f) => f.key).filter((key) => !orphaned.includes(key));
    const retryable = failed.some((f) => !orphaned.includes(f.key) && canRetry(f.error));
    if (uploaded > 0 || !retryable) retryAttempt.current = 0;
    if (retryable) scheduleRetry.current();
    orphanKeys.current = new Set(orphaned);
    setStatus((s) => dropped(drained(s, failedKeys), orphaned));
    const found: QueuedPhoto[] = [];
    for (const photoId of orphaned) {
      const bytes = await store.get(photoId);
      if (bytes) found.push({ photoId, bytes, queuedAt: store.queuedAt(photoId) });
    }
    // Discard takes a key out of orphanKeys, so one discarded after its read stays gone.
    setOrphans(found.filter((entry) => orphanKeys.current.has(entry.photoId)));
  };

  // One drain at a time, across tabs too: overlapping drains list the same entries and upload them
  // twice. A request during a drain queues one more pass, run against the current queue.
  const running = useRef<Promise<void> | null>(null);
  const again = useRef(false);
  const drain = useCallback((): Promise<void> => {
    if (running.current) {
      again.current = true;
      return running.current;
    }
    running.current = (async () => {
      do {
        again.current = false;
        try {
          await withDrainLock(() => pass.current());
        } catch (err) {
          console.error('photo upload pass failed', err);
          scheduleRetry.current();
        }
      } while (again.current);
    })().finally(() => {
      running.current = null;
    });
    return running.current;
  }, []);

  // Re-run for a new queue (graph change) or when signing in makes uploads possible.
  // biome-ignore lint/correctness/useExhaustiveDependencies: queue and canUpload are triggers; drain reads them through pass
  useEffect(() => {
    void drain();
  }, [drain, queue, canUpload]);

  const retryNow = useCallback(() => {
    retryAttempt.current = 0;
    void drain();
  }, [drain]);

  useEffect(() => {
    window.addEventListener('online', retryNow);
    return () => window.removeEventListener('online', retryNow);
  }, [retryNow]);

  const rowArrived = useCallback(
    (photoId: string) =>
      storedGroup('item_photo', photoId).then(
        () => true,
        (err: unknown) => {
          if (err instanceof MissingRowError) return false;
          throw err;
        },
      ),
    [storedGroup],
  );

  // Anonymous photos become this account's once the claim has copied their rows, which can
  // land after the mount drain, and a sync can bring back an orphan's row. A drain re-sends
  // every failed entry, so a sync that brought neither must not drain.
  useEffect(
    () =>
      onSynced(() => {
        void (async () => {
          const moved = await store.adopt();
          const back = await Promise.all([...orphanKeys.current].map(rowArrived));
          if (moved > 0 || back.includes(true)) void drain();
        })().catch((err) => console.warn('photo outbox sync check failed', err));
      }),
    [store, rowArrived, drain],
  );

  // The raw store, not the queue's reporting wrapper: a preview read is not an upload start.
  const localBytes = useCallback((photoId: string) => store.get(photoId), [store]);

  // The row may have arrived since the drawer opened; then the bytes are a real photo's only copy.
  const discard = useCallback(
    async (photoId: string) => {
      if (await rowArrived(photoId)) {
        void drain();
        return;
      }
      await store.delete(photoId);
      orphanKeys.current = new Set([...orphanKeys.current].filter((key) => key !== photoId));
      setOrphans((o) => o.filter((entry) => entry.photoId !== photoId));
    },
    [store, rowArrived, drain],
  );

  const value = useMemo<PhotoUploads>(
    () => ({
      enqueue: async (photoId, bytes) => {
        await queue.enqueue(photoId, bytes);
        setStatus((s) => enqueued(s, photoId));
        void drain();
      },
      retry: retryNow,
      stateOf: (photoId) => status[photoId] ?? (listedOnce ? undefined : 'checking'),
      localBytes,
      pending: pendingCount(status),
      failed: failedCount(status),
      orphans,
      discard,
      canUpload,
    }),
    [queue, drain, retryNow, status, listedOnce, localBytes, orphans, discard, canUpload],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function usePhotoUploads(): PhotoUploads {
  const value = useContext(Context);
  if (!value) throw new Error('usePhotoUploads needs a PhotoUploadsProvider');
  return value;
}

/**
 * Header counter: a fixed-width slot that is blank at zero, so the header never
 * shifts. Failed uploads turn it into a retry button. Orphans get their own button beside it.
 */
export function UploadIndicator() {
  const { pending, failed, orphans, canUpload, retry } = usePhotoUploads();
  const [reviewing, setReviewing] = useState(false);
  const show = canUpload && pending > 0;
  const n = orphans.length;
  if (reviewing && n === 0) setReviewing(false);
  return (
    <>
      {n > 0 && (
        <button
          type="button"
          onClick={() => setReviewing(true)}
          aria-label={`${n} queued ${n === 1 ? 'photo' : 'photos'} can't upload, review`}
          title="Photos that can't upload"
          className="rounded-lg px-1.5 py-0.5 text-sm font-medium text-red-700 hover:bg-amber-100"
        >
          ⚠{n}
        </button>
      )}
      {reviewing && <OrphanDrawer onClose={() => setReviewing(false)} />}
      <span className="inline-flex w-12 shrink-0 justify-center">
        {show && failed > 0 && (
          <button
            type="button"
            onClick={retry}
            aria-label={`${failed} photo uploads failed, retry`}
            title="Uploads failed, tap to retry"
            className="rounded-lg px-1.5 py-0.5 text-sm font-medium text-red-700 hover:bg-amber-100"
          >
            ↑{pending}!
          </button>
        )}
        {show && failed === 0 && (
          <output aria-label={`${pending} photos uploading`} className="text-sm text-amber-900">
            ↑{pending}
          </output>
        )}
      </span>
    </>
  );
}

const sizeText = (bytes: number) =>
  bytes < 1e6 ? `${Math.max(1, Math.round(bytes / 1e3))} kB` : `${(bytes / 1e6).toFixed(1)} MB`;

function OrphanDrawer({ onClose }: { onClose: () => void }) {
  const { orphans } = usePhotoUploads();
  return (
    <Drawer title="Photos that can't upload" onClose={onClose}>
      <Muted>
        These photos are waiting on this device, but the item they belong to isn't here, so they
        can't upload. Discard deletes them from this device.
      </Muted>
      <ul className="space-y-2">
        {orphans.map((entry, i) => (
          <OrphanEntry key={entry.photoId} entry={entry} n={i + 1} />
        ))}
      </ul>
    </Drawer>
  );
}

function OrphanEntry({ entry, n }: { entry: QueuedPhoto; n: number }) {
  const { discard } = usePhotoUploads();
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    const objectUrl = URL.createObjectURL(entry.bytes);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [entry.bytes]);
  const onDiscard = () => {
    if (!window.confirm('Discard this photo? Its only copy is on this device.')) return;
    discard(entry.photoId).catch((err) => console.error('discarding queued photo failed', err));
  };
  return (
    <li className="flex items-center gap-3">
      <img src={url} alt="" className="h-14 w-14 shrink-0 rounded-lg bg-gray-200 object-cover" />
      <div className="min-w-0 flex-1 text-sm text-content-secondary">
        <p>
          {entry.queuedAt === undefined
            ? 'Queued at an unknown time'
            : `Queued ${new Date(entry.queuedAt).toLocaleString()}`}
        </p>
        <p>{sizeText(entry.bytes.size)}</p>
      </div>
      <Button variant="danger" aria-label={`Discard queued photo ${n}`} onClick={onDiscard}>
        Discard
      </Button>
    </li>
  );
}
